/**
 * Edit a lead's vehicle of interest.
 *
 * - Free-text make + model are the primary fields (they're what the AI
 *   discovery flow captures and what shows on the Lead card).
 * - "Link to inventory" is an optional searchable dropdown. Picking a
 *   row auto-fills make + model from the chosen vehicle so the consultant
 *   doesn't have to retype.
 *
 * The backend treats a null field as "leave unchanged" and an empty
 * string as "clear" — this modal sends explicit `""` to clear and the
 * value to update.
 */
import { useEffect, useState } from 'react';
import { vehiclesService } from '../lib/services';

function unwrapVehicleList(resp) {
  return resp?.data?.content
      ?? resp?.content
      ?? (Array.isArray(resp?.data) ? resp.data : [])
      ?? (Array.isArray(resp) ? resp : []);
}

export default function VehicleInterestModal({ open, lead, onClose, onSave }) {
  const [make,  setMake]  = useState('');
  const [model, setModel] = useState('');
  const [vehicleId, setVehicleId] = useState('');
  const [inventory, setInventory] = useState([]);
  const [search,    setSearch]    = useState('');
  const [busy,      setBusy]      = useState(false);
  const [error,     setError]     = useState(null);

  // Every vehicle this dialog has seen, by id, so the "linked vehicle" label and
  // the make/model drift check work whichever search results are on screen.
  const [known, setKnown] = useState({});
  const remember = (list) => setKnown(prev => {
    const next = { ...prev };
    list.forEach(v => { if (v?.id) next[String(v.id)] = v; });
    return next;
  });

  // Seed from the lead when the dialog OPENS (or switches to another lead) —
  // NOT whenever the lead object changes. The lead page re-fetches the lead
  // every few seconds, which hands this component a new object each time; seeding
  // on that wiped whatever the consultant had typed or picked within ~3 seconds.
  useEffect(() => {
    if (!open) return;
    setMake (lead?.vehicleMakeRequested  ?? '');
    setModel(lead?.vehicleModelRequested ?? '');
    setVehicleId(lead?.vehicleOfInterestId || '');
    setSearch('');
    setError(null);
    setBusy(false);
    // Fetch the currently linked vehicle so its label shows and a later edit of
    // make/model can be checked against it.
    const linked = lead?.vehicleOfInterestId;
    if (linked) {
      vehiclesService.get(linked)
        .then(r => { const v = r?.data ?? r; if (v?.id) remember([v]); })
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, lead?.id]);

  // Inventory picker: searched on the server (the `q` parameter) as the
  // consultant types, so any vehicle can be found — not just the 50 cheapest
  // that used to be loaded and filtered in the browser.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const t = setTimeout(() => {
      const q = search.trim();
      vehiclesService.list({ page: 0, limit: 30, ...(q ? { q } : {}) })
        .then(r => {
          if (cancelled) return;
          const rows = unwrapVehicleList(r);
          remember(rows);
          setInventory(rows);
        })
        .catch(() => { if (!cancelled) setInventory([]); });
    }, search ? 250 : 0);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, search]);

  // Esc to close.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const onPickVehicle = (v) => {
    remember([v]);
    setVehicleId(v.id);
    // Auto-fill make + model so the consultant doesn't have to retype.
    if (v.make)  setMake (v.make);
    if (v.model) setModel(v.model);
  };
  const onClearVehicle = () => setVehicleId('');

  // Typing a make/model that no longer matches the linked vehicle drops the
  // link — otherwise the lead ends up saying "Toyota BMW" while pointing at a
  // different stock car.
  const sameText = (a, b) => String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();
  const editMake = (value) => {
    setMake(value);
    const v = vehicleId ? known[String(vehicleId)] : null;
    if (v && !sameText(value, v.make)) setVehicleId('');
  };
  const editModel = (value) => {
    setModel(value);
    const v = vehicleId ? known[String(vehicleId)] : null;
    if (v && !sameText(value, v.model)) setVehicleId('');
  };

  const submit = async (e) => {
    e?.preventDefault();
    setBusy(true);
    setError(null);
    try {
      // Send explicit empty strings when the user cleared the text; the
      // backend will treat those as "set to null".
      const body = {
        vehicleMakeRequested:  make,
        vehicleModelRequested: model,
      };
      if (vehicleId) {
        body.vehicleOfInterestId    = vehicleId;
        body.clearVehicleOfInterest = false;
      } else if (lead?.vehicleOfInterestId) {
        // User cleared a previously-linked vehicle.
        body.clearVehicleOfInterest = true;
      }
      await onSave?.(body);
      onClose?.();
    } catch (err) {
      const apiMsg =
        err?.response?.data?.message
        || err?.response?.data?.error
        || err?.message
        || 'Save failed';
      setError(apiMsg);
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="veh-int-title"
      onClick={onClose}
      className="modal-backdrop"
    >
      <form
        className="modal"
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        style={{ width: 'min(620px, 92vw)' }}
      >
        <div className="modal-header">
          <div>
            <div id="veh-int-title" style={{ fontSize: 16, fontWeight: 600 }}>
              Edit vehicle interest
            </div>
            <div className="mono text-xs text-muted" style={{ marginTop: 2 }}>
              Update the make / model the customer is after, and link to a
              specific inventory row if you've picked one out.
            </div>
          </div>
          <button
            type="button"
            className="modal-close"
            onClick={onClose}
            aria-label="Close"
          >✕</button>
        </div>

        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="grid-2" style={{ gap: 12 }}>
            <div className="form-field">
              <label htmlFor="vi-make">Make</label>
              <input
                id="vi-make"
                value={make}
                onChange={(e) => editMake(e.target.value)}
                placeholder="Toyota"
                autoFocus
              />
            </div>
            <div className="form-field">
              <label htmlFor="vi-model">Model</label>
              <input
                id="vi-model"
                value={model}
                onChange={(e) => editModel(e.target.value)}
                placeholder="Hilux"
              />
            </div>
          </div>

          <div className="form-field">
            <label>Link to inventory <span className="text-xs text-muted">(optional)</span></label>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search make, model, VIN, stock #…"
            />
            <div
              style={{
                marginTop: 8,
                border: '1px solid var(--border-subtle)',
                borderRadius: 'var(--radius-sm)',
                maxHeight: 220,
                overflowY: 'auto',
                background: 'var(--bg-glass)',
              }}
            >
              {inventory.length === 0 && (
                <div className="empty-state" style={{ padding: 14 }}>
                  <div className="mono text-xs text-muted">
                    {search ? 'No matches — try a different make/model.' : 'Inventory is empty.'}
                  </div>
                </div>
              )}
              {inventory.map(v => {
                const picked = String(v.id) === String(vehicleId);
                return (
                  <button
                    key={v.id}
                    type="button"
                    onClick={() => onPickVehicle(v)}
                    className={`crm-row ${picked ? 'active' : ''}`}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 70px 90px 110px',
                      gap: 8,
                      width: '100%',
                      textAlign: 'left',
                      padding: '8px 12px',
                      background: picked ? 'var(--accent-glow)' : 'transparent',
                      border: 'none',
                      borderBottom: '1px solid var(--border-subtle)',
                      cursor: 'pointer',
                      color: 'var(--text-primary)',
                    }}
                  >
                    <span style={{ fontSize: 13, fontWeight: 500 }}>
                      {v.make} {v.model}{v.trim ? ` ${v.trim}` : ''}
                    </span>
                    <span className="mono text-xs text-muted">{v.year ?? '—'}</span>
                    <span className="mono text-xs text-muted">{v.condition || '—'}</span>
                    <span className="mono text-xs text-muted" style={{ textAlign: 'right' }}>
                      {v.dmsStockId || (v.vin ? `VIN ${v.vin.slice(-6)}` : '—')}
                    </span>
                  </button>
                );
              })}
            </div>
            {vehicleId && (() => {
              const v = known[String(vehicleId)];
              const label = v
                ? `${[v.year, v.make, v.model, v.trim].filter(Boolean).join(' ')}${v.dmsStockId ? ` · ${v.dmsStockId}` : ''}`
                : 'a stock vehicle';
              return (
                <div style={{ marginTop: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                  <span className="mono text-xs text-muted" style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    Linked vehicle: {label}
                  </span>
                  <button type="button" className="btn small" onClick={onClearVehicle}>
                    Disconnect
                  </button>
                </div>
              );
            })()}
          </div>

          {error && (
            <div style={{
              padding: '8px 12px',
              background: 'rgba(239,68,68,0.08)',
              border: '1px solid var(--danger)',
              borderRadius: 6,
              color: 'var(--danger)',
              fontSize: 12,
            }}>
              {error}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button type="button" className="btn small" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn small primary" disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </div>
  );
}
